import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, FormikHelpers, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { frenchTerritoriesOptions } from '../../../../resources/base/models/country';
import {
  CitiesAndZipCodesSuggestion,
  localitiesWithZipCodes,
  zipCodesForLocality,
} from '../../../../services/api/address/localitiesAndZipCodes';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { validateEmailTls } from '../../utils/checkTLSUtil';
import {
  BirthCountry,
  BirthName,
  CountryCodeMobilePhone,
  DateOfBirth,
  Email,
  FirstName,
  Gender,
  LastName,
  MobilePhone,
  Nationality,
  PlaceOfBirth,
  Profession,
  ResidenceStatus,
  TelephonePhone,
  ZipCodeOfBirth,
} from '../../utils/fieldsPrivateCustomer';
import { formatCityNameLettersAndSpaceOnly } from '../../utils/formatCityString';
import {
  CustomerDetailsFormGroupForms,
  DisabledCreditApplicationBirthInformationFields,
  getDisabledFields,
  patchCreditApplicationAndUpdateState,
  useFormSectionHelpers,
} from '../utils';
import { ContactDataFormType, getContactDataFormInitialValues } from './initialValues';
import { contactDataFormRequestMapper } from './requestMapper';
import { getContactDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'coCustomer'>;
}

export const ContactDataForm: FunctionComponent<Props> = ({ formName }) => {
  const [initialValues, setInitialValues] = useState({} as ContactDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<ContactDataFormType>>();

  const creditApplicantCustomerData = useSelector(
    creditApplicationSelectors.selectCreditApplicantCustomerDataCoCustomer
  ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;

  const isExistingClient = useSelector(quoteSelectors.selectPreviousContractId) !== undefined;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setInitialValues(getContactDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getContactDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    'coCustomer',
    formName
  );

  const onSubmit = async (values: ContactDataFormType, { setFieldError }: FormikHelpers<ContactDataFormType>) => {
    const tlsValid = await validateEmailTls(values.email!, setFieldError, dispatch);

    if (!tlsValid) {
      return;
    }

    dispatch(loadingActions.setLoading(true));
    patchCreditApplicationAndUpdateState(contactDataFormRequestMapper(values, creditApplicantCustomerData), true)
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  // Fields must remain editable if the API provides no value; otherwise, the user cannot change the input.
  const disabledFields = useMemo(
    () => getDisabledFields(creditApplicantCustomerData, isExistingClient),
    [creditApplicantCustomerData, isExistingClient]
  );

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:contactInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} disabledFields={disabledFields} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
  disabledFields: DisabledCreditApplicationBirthInformationFields;
}

const FormContent: FunctionComponent<FormContentProps> = ({ disabledFields }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, submitForm: _submitForm } = useFormikContext<ContactDataFormType>();

  const [suggestionsForPlacefBirth, setSuggestionsForPlaceOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedLocationsLoading, setSuggestedLocationsLoading] = useState(false);
  const [suggestionsForZipCodeOfBirth, setSuggestionsForZipCodeOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedZipCodesLoading, setSuggestedZipCodesLoading] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const isFrenchTerritory = frenchTerritoriesOptions.some((option) => option.value === values.birthCountry);

  const submitForm = async () => {
    await _submitForm();
  };

  const onSelectSuggestion = (suggestion: string) => {
    const zipMatch = suggestion.match(/\b\d{5}\b/);
    const zip = zipMatch ? zipMatch[0] : null;
    const city = zip ? suggestion.replace(zip, '').trim() : suggestion.trim();

    setIsUserTyping(false);
    if (isFrenchTerritory) {
      setFieldValue('zipCodeOfBirth', zip);
    }
    setFieldValue('placeOfBirth', formatCityNameLettersAndSpaceOnly(city));
  };

  const onZipCodeOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('zipCodeOfBirth', value);
  };

  const onPlaceOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('placeOfBirth', value);
  };

  useEffect(() => {
    if (!values.zipCodeOfBirth || values.zipCodeOfBirth?.length < 5) return;

    async function fetchSuggestions() {
      try {
        setSuggestedZipCodesLoading(true);
        const res = await localitiesWithZipCodes(values.zipCodeOfBirth || '');
        setSuggestionsForZipCodeOfBirth(res);
        setSuggestionsForPlaceOfBirth([]);
      } catch {
        setSuggestedZipCodesLoading(false);
      } finally {
        setSuggestedZipCodesLoading(false);
      }
    }

    fetchSuggestions();
  }, [values.zipCodeOfBirth]);

  useEffect(() => {
    if (!values.placeOfBirth || values.placeOfBirth?.length < 3) return;

    async function fetchSuggestions() {
      try {
        setSuggestedLocationsLoading(true);
        const res = await zipCodesForLocality(values.placeOfBirth || '');
        setSuggestionsForPlaceOfBirth(res);
        setSuggestionsForZipCodeOfBirth([]);
      } catch {
        setSuggestedLocationsLoading(false);
      } finally {
        setSuggestedLocationsLoading(false);
      }
    }

    const handler = setTimeout(() => {
      fetchSuggestions();
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(handler);
    };
  }, [values.placeOfBirth]);

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <Gender t={t} disabled />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <FirstName t={t} disabled />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <LastName t={t} disabled />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <BirthName t={t} disabled />
          </Layout.Item>
          <Layout.Item default="1/5" m="2/5" s="1/1">
            <DateOfBirth
              setFieldValue={setFieldValue}
              values={values}
              labelText={t('step2:dateOfBirth:label')}
              infoText={t('step2:dateInfoText:label')}
              disabled
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/4" s="1/1">
            <Nationality
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              name="nationality"
              disabled={disabledFields.nationality}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <BirthCountry
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              name="birthCountry"
              disabled={disabledFields.birthCountry}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <ZipCodeOfBirth
              t={t}
              name="zipCodeOfBirth"
              values={values}
              disabled={disabledFields.zipCodeOfBirth}
              onSelectSuggestion={onSelectSuggestion}
              onChange={onZipCodeOfBirthChange}
              suggestions={isUserTyping ? suggestionsForZipCodeOfBirth : []}
              isSectionLoading={suggestedZipCodesLoading}
              setFieldValue={setFieldValue}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <PlaceOfBirth
              t={t}
              name="placeOfBirth"
              values={values}
              disabled={disabledFields.placeOfBirth}
              onSelectSuggestion={onSelectSuggestion}
              onChange={onPlaceOfBirthChange}
              suggestions={isUserTyping ? suggestionsForPlacefBirth : []}
              isSectionLoading={suggestedLocationsLoading}
              setFieldValue={setFieldValue}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CountryCodeMobilePhone t={t} setFieldValue={setFieldValue} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <MobilePhone t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <TelephonePhone t={t} />
          </Layout.Item>
          <Layout.Item default="1/3" m="7/12" s="1/1">
            <Email t={t} />
          </Layout.Item>
          <Layout.Item default="1/3" m="5/12" s="1/1">
            <ResidenceStatus t={t} name="residenceStatus" />
          </Layout.Item>
          <Layout.Item default="1/2" m="2/3" s="1/1">
            <Profession t={t} setFieldValue={setFieldValue} values={values} disabled />
          </Layout.Item>
          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
